import { useState, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { rtkqErrorToString } from "../../../app/rtkQueryHelper";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import { Loading } from "../../../components/Loading";
import Select from "../../../components/input/Select";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { UserLogViewModel, useUserLogQuery } from "../usersApiSlice";

interface UserLogProps {
	userId: number;
	setIsDialogOpen: (isOpen: boolean) => void;
}

// Keyword categories for filtering
const ACTION_CATEGORIES = [
	{ label: "All Actions", value: "all", keywords: [] },
	{ label: "Login Activity", value: "login", keywords: ["login", "signed out"] },
	{
		label: "Password Changed",
		value: "password",
		keywords: ["password changed", "changed the password", "changes the password"],
	},
	{ label: "Impersonation", value: "impersonate", keywords: ["impersonate"] },
	{
		label: "Writing Activity",
		value: "writing",
		keywords: ["essay", "organizer", "draft", "submit", "finished", "save and quit"],
	},
	{ label: "Creation", value: "create", keywords: ["create"] },
	{ label: "Merge Users", value: "merge", keywords: ["merged"] },
	{ label: "Features", value: "feature", keywords: ["feature"] },
];

export default function UserLog(props: UserLogProps) {
	const [actionFilter, setActionFilter] = useState<string>("all");
	const [courseFilter, setCourseFilter] = useState<string>("all");
	const [filteredUserLog, setFilteredUserLog] = useState<UserLogViewModel[]>();

	const userLogResult = useUserLogQuery(props.userId);
	const userLog = userLogResult.data;

	const hasLogWithCourse = userLog?.some((row) => row.course);

	// Only show the categories that have a keyword match with at least one row
	const availableCategories = useMemo(() => {
		if (!userLog) return [];

		const available = ACTION_CATEGORIES.filter((category) => {
			if (category.value === "all") return true;

			return userLog.some((row) => {
				const actionTypeLower = row.actionType.toLowerCase();
				return category.keywords.some((keyword) => actionTypeLower.includes(keyword));
			});
		});

		return available.map((cat) => ({ label: cat.label, value: cat.value }));
	}, [userLog]);

	// Only show courses in the dropdown with the course name that comes before (id: #)
	const availableCourses = useMemo(() => {
		if (!userLog) return [];

		const uniqueCourses = Array.from(
			new Set(userLog.map((row) => row.course?.split(" (id:")[0].trim()).filter(Boolean))
		).sort();

		return [
			{ label: "All Courses", value: "all" },
			...uniqueCourses.map((course) => ({ label: course, value: course })),
		];
	}, [userLog]);

	useEffect(() => {
		if (userLog) {
			let filtered = userLog;

			if (actionFilter !== "all") {
				const category = ACTION_CATEGORIES.find((cat) => cat.value === actionFilter);
				if (category) {
					filtered = filtered.filter((row) => {
						const actionTypeLower = row.actionType.toLowerCase();
						return category.keywords.some((keyword) =>
							actionTypeLower.includes(keyword)
						);
					});
				}
			}

			if (courseFilter !== "all") {
				filtered = filtered.filter((row) => {
					return row.course?.split(" (id:")[0].trim() === courseFilter;
				});
			}

			setFilteredUserLog(filtered);
		}
	}, [actionFilter, courseFilter, userLog]);

	function handleCloseDialog() {
		props.setIsDialogOpen(false);
		setActionFilter("all");
		setCourseFilter("all");
	}

	const rowHeight = 65; //average row height size
	const maxHeight = 500;
	const rowCount = filteredUserLog?.length ?? 0;
	const currentHeight = (rowCount === 0 ? 1 : rowCount) * rowHeight + 45; //added 45 for the header to always appear

	const dynamicScrollHeight = `${Math.min(currentHeight, maxHeight)}px`;

	return (
		<section>
			{userLogResult.isError ? (
				<AlertMessage
					type={AlertLevel.Error}
					message={rtkqErrorToString(userLogResult.error)}
					showIcon
					className="mt-4"
				/>
			) : (
				<>
					<Loading
						loading={userLogResult.isLoading || !filteredUserLog}
						className="mt-4! h-[500px]"
					/>
					{userLog && filteredUserLog && (
						<>
							<div className="mt-4 flex gap-4">
								{availableCategories.length > 1 && (
									<Select
										value={actionFilter}
										setValue={(v) => setActionFilter(v as string)}
										label="Action Category"
										simpleListBoxId="actionCategoryFilter"
										options={availableCategories}
										simpleListBoxClasses="w-72"
									/>
								)}
								{hasLogWithCourse && (
									<Select
										value={courseFilter}
										setValue={(v) => setCourseFilter(v as string)}
										label="Course"
										simpleListBoxId="courseFilter"
										options={availableCourses}
										simpleListBoxClasses="w-72"
									/>
								)}
							</div>
							<DataTable
								value={filteredUserLog}
								dataKey="indexId"
								sortIcon={CustomSortIcon}
								stripedRows
								emptyMessage="No results found"
								className="tbl tbl-data tbl-datatable sticky-datatable-header mt-4"
								scrollable
								scrollHeight={dynamicScrollHeight}
								virtualScrollerOptions={{
									itemSize: 65,
									autoSize: true,
									numToleratedItems: 10,
									delay: 0,
								}}>
								<Column
									field="actionTime"
									header="Time"
									sortable
									className="!text-sm !w-16"
									body={(row: UserLogViewModel) => (
										<FormattedDateAndTime date={row.actionTime} />
									)}
								/>
								<Column
									field="actionType"
									header="Action"
									sortable
									className="!text-sm"
								/>
								{hasLogWithCourse && (
									<Column
										field="course"
										header="Course"
										sortable
										className="!text-sm"
									/>
								)}
								<Column
									field="ipAddress"
									header="IP"
									sortable
									className="!text-sm"
								/>
								<Column field="os" header="OS" sortable className="!text-sm" />
								<Column
									field="device"
									header="Device"
									sortable
									className="!text-sm"
								/>
								<Column
									field="browser"
									header="Browser"
									sortable
									className="!text-sm"
								/>
							</DataTable>
						</>
					)}
				</>
			)}
			<div className="mt-4 w-full">
				<button
					type="button"
					id="close-log-button"
					onClick={handleCloseDialog}
					className="btn btn-neutral w-full">
					Close
				</button>
			</div>
		</section>
	);
}
